import { useState } from "react";
import { ArrowRight, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { apiClient } from "../lib/api";
import { SocialLinks } from "../components/SocialLinks";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out every field.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Message sent. Thank you!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="relative py-16 md:py-24 px-6" data-testid="contact-page">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="seal mb-6">
              <MessageSquare size={12} /> Get In Touch
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6 leading-tight"
              data-testid="contact-heading"
            >
              I'm listening.
            </h1>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10">
              Have a question or concern? Send me a message and I'll get back
              to you. Your voice matters — that's what serving Vineyard is all
              about.
            </p>

            <a
              href="mailto:jacobw@vineyardutah.gov"
              className="flex items-center gap-3 glass rounded-xl p-4 hover-lift group"
              data-testid="contact-email-link"
            >
              <div className="w-10 h-10 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center text-yellow-400">
                <Mail size={18} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-1">
                  Email
                </p>
                <p className="text-white group-hover:text-yellow-400 transition-colors text-sm">
                  jacobw@vineyardutah.gov
                </p>
              </div>
            </a>

            <div className="mt-6 flex items-center gap-3">
              <span className="text-xs uppercase tracking-[0.2em] text-white/40 mr-1">Follow</span>
              <SocialLinks className="!justify-start" />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="glass-strong rounded-2xl p-6 md:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input
                  type="text"
                  placeholder="NAME"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 placeholder:text-sm placeholder:tracking-[0.2em] focus:outline-none focus:border-yellow-400/50 transition-colors"
                  data-testid="contact-name"
                />
                <input
                  type="email"
                  placeholder="EMAIL"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 placeholder:text-sm placeholder:tracking-[0.2em] focus:outline-none focus:border-yellow-400/50 transition-colors"
                  data-testid="contact-email"
                />
              </div>
              <textarea
                placeholder="YOUR MESSAGE"
                rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-white/5 border border-white/15 rounded-xl px-5 py-4 text-white placeholder:text-white/40 placeholder:text-sm placeholder:tracking-[0.2em] focus:outline-none focus:border-yellow-400/50 transition-colors resize-none"
                data-testid="contact-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 btn-accent px-7 py-4 text-[15px] disabled:opacity-60"
                data-testid="contact-submit"
              >
                <span>{submitting ? "Sending…" : "Send Message"}</span>
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}
